import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useTheme } from '../contexts/ThemeContext';
import { 
  ArrowRight, 
  ShieldCheck, 
  Lightning, 
  ChartLine,
  UsersThree,
  Package,
  CheckCircle,
  Star,
  Moon,
  Sun,
  CurrencyDollar,
  Handshake,
  Gift,
  Percent,
  Megaphone,
  Sparkle,
} from '@phosphor-icons/react';

const HERO_IMG = 'https://static.prod-images.emergentagent.com/jobs/ac56158f-922f-420f-81a7-5ab29adffbb4/images/e5fafcb8773d2e4891fea26f3ecfb53608be5a3952d10f0e426996747e9d7f16.png';

const LandingPage = () => {
  const { isDark, toggleTheme } = useTheme();

  const handleApkDownload = async (e) => {
    e.preventDefault();
    const href = e.currentTarget.getAttribute('href');
    try {
      const res = await fetch(href, { method: 'HEAD' });
      const ct = (res.headers.get('content-type') || '').toLowerCase();
      // If the file exists it should NOT be served as HTML (SPA fallback)
      if (res.ok && !ct.includes('text/html')) {
        window.location.href = href;
        return;
      }
    } catch (_) {
      // fall through to the "coming soon" message
    }
    alert(
      'La App Móvil para Android estará disponible muy pronto.\n\n' +
      'Por ahora, puedes descargar MaterMatch desde Huawei AppGallery ' +
      'o esperar a que publiquemos la versión estable en esta misma página.'
    );
  };

  const features = [
    { icon: Package, title: 'Cotizaciones Inteligentes', description: 'Crea proyectos con múltiples materiales y recibe propuestas de proveedores especializados automáticamente.' },
    { icon: UsersThree, title: 'Red de Proveedores', description: 'Accede a una amplia red de proveedores verificados de materiales de construcción en todo México.' },
    { icon: ChartLine, title: 'Consultor de Precios', description: 'Consulta precios históricos de materiales por región para tomar mejores decisiones de compra.' },
    { icon: ShieldCheck, title: 'Transacciones Seguras', description: 'Proceso de contratación transparente con verificación fiscal y sistema de calificaciones.' },
    { icon: Lightning, title: 'Proceso Rápido', description: 'Recibe propuestas en minutos, no en días. Compara y selecciona la mejor opción fácilmente.' },
    { icon: Star, title: 'Calificaciones Reales', description: 'Sistema de calificación basado en transacciones reales para garantizar la calidad del servicio.' },
  ];

  const stats = [
    { value: '5,000+', label: 'Proveedores Activos' },
    { value: '$50M+', label: 'En Transacciones' },
    { value: '10,000+', label: 'Proyectos Completados' },
    { value: '32', label: 'Estados de México' },
  ];

  const plans = [
    {
      name: 'Mensual',
      price: '$395',
      period: '/mes',
      totalLabel: '$458.20 MXN con IVA',
      discount: null,
      features: ['Acceso completo al consultor de precios', 'Historial de 2 años por material', 'Comparativa por estados', 'Alertas de precio por email', 'Descarga de reportes en PDF'],
      popular: false,
    },
    {
      name: 'Semestral',
      price: '$355',
      period: '/mes',
      totalLabel: '$2,474.28 MXN con IVA (6 meses)',
      discount: '10% de descuento',
      features: ['Todo lo del plan Mensual', 'Ahorra $237 vs. pago mensual', 'Soporte prioritario', 'Alertas ilimitadas', 'Descarga de reportes en PDF'],
      popular: true,
    },
    {
      name: 'Anual',
      price: '$316',
      period: '/mes',
      totalLabel: '$4,398.72 MXN con IVA (12 meses)',
      discount: '20% de descuento',
      features: ['Todo lo del plan Semestral', 'Ahorra $1,098 vs. pago mensual', 'Acceso anticipado a nuevas funciones', 'Reportes avanzados de mercado', 'Descarga de reportes en PDF'],
      popular: false,
    },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img 
                src="/matermatch-logo.png" 
                alt="MaterMatch" 
                className="h-10 sm:h-12 w-auto"
                data-testid="landing-logo"
              />
              <div className="hidden sm:block">
                <span className="text-xl font-black text-zinc-600 dark:text-zinc-300 tracking-tight">Mater</span>
                <span className="text-xl font-black text-orange-500 tracking-tight">Match</span>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="text-zinc-600 dark:text-zinc-400"
                data-testid="landing-theme-toggle"
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>
              <Link to="/login" className="text-sm sm:text-base text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 font-medium" data-testid="landing-login">
                Iniciar Sesión
              </Link>
              <Link to="/register" data-testid="landing-register">
                <Button className="btn-primary rounded-none text-sm sm:text-base px-3 sm:px-4">
                  Comenzar
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Launch Banner */}
      <div className="bg-gradient-to-r from-orange-500 via-orange-600 to-amber-500 py-3 px-4" data-testid="launch-banner">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 sm:gap-3 flex-wrap text-center">
          <Sparkle className="w-5 h-5 text-white animate-pulse hidden sm:block" weight="fill" />
          <span className="text-white text-sm sm:text-base font-bold">
            Por lanzamiento, el acceso es completamente GRATIS por tiempo limitado
          </span>
          <span className="text-white/90 text-xs sm:text-sm">
            &mdash; Sin comisiones &middot; Sin suscripciones &middot; Todas las funciones
          </span>
          <Sparkle className="w-5 h-5 text-white animate-pulse hidden sm:block" weight="fill" />
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={HERO_IMG}
            alt="Profesionales cerrando un trato en construcción"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/95 via-zinc-950/80 to-zinc-950/40"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/30 px-3 sm:px-4 py-1.5 sm:py-2 mb-4 sm:mb-6">
              <Lightning className="w-4 h-4 text-orange-400" />
              <span className="text-orange-400 text-xs sm:text-sm font-medium">Plataforma B2B #1 en México</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-3 sm:mb-4 leading-tight">
              <span className="text-orange-400">Mater</span>Match
            </h1>
            <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-white/90 mb-4 sm:mb-6 tracking-wide">
              Cotiza. Compara. Construye.
            </p>
            
            <p className="text-base sm:text-lg text-zinc-300 mb-6 sm:mb-8 max-w-xl">
              Simplifica tus compras de materiales de construcción. Recibe cotizaciones de múltiples proveedores verificados y elige la mejor opción para tu proyecto.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link to="/register" data-testid="hero-cta">
                <Button className="btn-primary rounded-none h-12 sm:h-14 px-6 sm:px-8 text-base sm:text-lg w-full sm:w-auto">
                  Comenzar Ahora
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="rounded-none h-12 sm:h-14 px-6 sm:px-8 text-base sm:text-lg border-white/30 text-white hover:bg-white/10 w-full sm:w-auto">
                  Ya tengo cuenta
                </Button>
              </Link>
            </div>

            {/* App download CTAs */}
            <div className="mt-8 sm:mt-10 border-t border-white/10 pt-6 sm:pt-8">
              <p className="text-xs sm:text-sm text-zinc-400 mb-3 uppercase tracking-wide">
                También disponible como App Móvil para Android
              </p>
              <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                <a
                  href="https://appgallery.cloud.huawei.com/ag/n/app/C117357949?locale=es_US"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="download-huawei-btn"
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 px-4 py-3 text-sm text-white transition-colors"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 3C8 3 4 6 3 10l9-2 9 2c-1-4-5-7-9-7zm-9 11c1 4 5 7 9 7s8-3 9-7l-9 2-9-2z"/></svg>
                  Descargar en Huawei AppGallery
                </a>
                <a
                  href="/downloads/matermatch-latest.apk"
                  onClick={handleApkDownload}
                  download
                  data-testid="download-apk-btn"
                  className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 border border-orange-500 px-4 py-3 text-sm text-white transition-colors"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 16l-6-6h4V4h4v6h4l-6 6zM5 20h14v-2H5v2z"/></svg>
                  Descargar App Móvil para Android
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-zinc-950 py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-orange-500 mb-1 sm:mb-2">{stat.value}</div>
                <div className="text-zinc-400 text-xs sm:text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing: Clients FREE / Providers 3% */}
      <section className="py-14 sm:py-20 bg-white dark:bg-zinc-950" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-14">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 dark:text-zinc-100 mb-3 sm:mb-4">
              Transparente y justo para todos
            </h2>
            <p className="text-base sm:text-lg text-zinc-600 dark:text-zinc-400 max-w-2xl mx-auto">
              MaterMatch conecta clientes y proveedores con un modelo claro y sin sorpresas
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto">
            {/* Client Card */}
            <div className="relative bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-950/30 dark:to-zinc-900 border-2 border-emerald-200 dark:border-emerald-800 p-6 sm:p-8 hover:shadow-[4px_4px_0px_0px_rgba(16,185,129,0.4)] transition-shadow" data-testid="client-pricing-card">
              <div className="absolute -top-3 left-6">
                <span className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 uppercase tracking-wider">
                  Clientes
                </span>
              </div>
              <div className="flex items-center gap-4 mb-6 mt-2">
                <div className="w-14 h-14 bg-emerald-500 flex items-center justify-center">
                  <Gift className="w-7 h-7 text-white" weight="bold" />
                </div>
                <div>
                  <div className="text-3xl sm:text-4xl font-black text-emerald-600 dark:text-emerald-400">GRATIS</div>
                  <div className="text-sm text-zinc-600 dark:text-zinc-400">Sin costo alguno</div>
                </div>
              </div>
              <ul className="space-y-3">
                {[
                  'Crear proyectos ilimitados',
                  'Recibir propuestas de proveedores',
                  'Comparar precios y condiciones',
                  'Seleccionar la mejor opción',
                  'Contacto directo con el proveedor',
                  'Sin comisiones ni tarifas ocultas',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm sm:text-base text-zinc-700 dark:text-zinc-300">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" weight="fill" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Provider Card */}
            <div className="relative bg-gradient-to-br from-orange-50 to-white dark:from-orange-950/30 dark:to-zinc-900 border-2 border-orange-200 dark:border-orange-800 p-6 sm:p-8 hover:shadow-[4px_4px_0px_0px_rgba(249,115,22,0.4)] transition-shadow" data-testid="provider-pricing-card">
              <div className="absolute -top-3 left-6">
                <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1 uppercase tracking-wider">
                  Proveedores
                </span>
              </div>
              <div className="absolute -top-3 right-6">
                <span className="bg-emerald-500 text-white text-xs font-bold px-3 py-1 uppercase tracking-wider animate-pulse">
                  Promo Lanzamiento
                </span>
              </div>
              <div className="flex items-center gap-4 mb-6 mt-2">
                <div className="w-14 h-14 bg-orange-500 flex items-center justify-center">
                  <Percent className="w-7 h-7 text-white" weight="bold" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl sm:text-3xl font-black text-zinc-400 line-through">3%</span>
                    <span className="text-3xl sm:text-4xl font-black text-emerald-600 dark:text-emerald-400">0%</span>
                  </div>
                  <div className="text-sm text-emerald-600 dark:text-emerald-400 font-semibold">Sin comisión por tiempo limitado</div>
                </div>
              </div>
              <ul className="space-y-3">
                {[
                  'Registrarse y cotizar es gratis',
                  '0% comisión durante el lanzamiento',
                  'Acceso a proyectos de tu especialidad',
                  'Contacto directo con el cliente',
                  'Sin cuotas mensuales obligatorias',
                  'Precio regular: 3% por venta exitosa',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm sm:text-base text-zinc-700 dark:text-zinc-300">
                    <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" weight="fill" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-14 sm:py-24 bg-zinc-100 dark:bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 dark:text-zinc-100 mb-3 sm:mb-4">
              Todo lo que necesitas para tus proyectos
            </h2>
            <p className="text-base sm:text-lg text-zinc-600 dark:text-zinc-400 max-w-2xl mx-auto">
              Una plataforma completa para gestionar tus compras de materiales de construcción
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 p-5 sm:p-8 hover:shadow-[4px_4px_0px_0px_rgba(9,9,11,1)] transition-shadow"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-500 flex items-center justify-center mb-4 sm:mb-6">
                  <feature.icon className="w-5 h-5 sm:w-6 sm:h-6 text-zinc-900" weight="bold" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-zinc-900 dark:text-zinc-100 mb-2 sm:mb-3">{feature.title}</h3>
                <p className="text-sm sm:text-base text-zinc-600 dark:text-zinc-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Subscription Plans Section */}
      <section className="py-14 sm:py-24 bg-white dark:bg-zinc-950" data-testid="subscription-plans-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-14">
            <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 px-4 py-2 mb-4">
              <ChartLine className="w-5 h-5 text-orange-500" />
              <span className="text-orange-600 dark:text-orange-400 text-sm font-semibold">Consultor de Precios</span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 dark:text-zinc-100 mb-3 sm:mb-4">
              Planes de Suscripción
            </h2>
            <p className="text-base sm:text-lg text-zinc-600 dark:text-zinc-400 max-w-2xl mx-auto">
              Accede a datos históricos de precios, tendencias del mercado y alertas personalizadas para tomar las mejores decisiones de compra
            </p>
          </div>

          {/* Launch Promo Notice */}
          <div className="max-w-3xl mx-auto mb-8 sm:mb-10 bg-gradient-to-r from-emerald-50 to-emerald-100 dark:from-emerald-950/40 dark:to-emerald-900/30 border-2 border-emerald-300 dark:border-emerald-700 p-5 sm:p-6" data-testid="subscription-launch-promo">
            <div className="flex items-center gap-3 mb-2">
              <Megaphone className="w-6 h-6 text-emerald-600 dark:text-emerald-400 flex-shrink-0" weight="fill" />
              <h3 className="text-lg sm:text-xl font-bold text-emerald-700 dark:text-emerald-300">
                Acceso GRATIS por lanzamiento
              </h3>
            </div>
            <p className="text-sm sm:text-base text-emerald-700/80 dark:text-emerald-400/80 ml-9">
              Durante nuestro periodo de lanzamiento, el Consultor de Precios es <strong>completamente gratis</strong> para todos los usuarios. Disfruta de todas las funciones sin costo alguno por tiempo limitado.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`relative flex flex-col p-6 sm:p-8 border-2 transition-shadow ${
                  plan.popular
                    ? 'border-orange-500 shadow-[4px_4px_0px_0px_rgba(249,115,22,1)]'
                    : 'border-zinc-200 dark:border-zinc-700 hover:shadow-[4px_4px_0px_0px_rgba(9,9,11,1)]'
                } bg-white dark:bg-zinc-900`}
                data-testid={`plan-${plan.name.toLowerCase()}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-orange-500 text-white text-xs font-bold px-4 py-1 uppercase tracking-wider whitespace-nowrap">
                      Más Popular
                    </span>
                  </div>
                )}
                {plan.discount && (
                  <div className="inline-flex self-start items-center gap-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-bold px-2 py-1 mb-4">
                    <Percent className="w-3 h-3" />
                    {plan.discount}
                  </div>
                )}
                {!plan.discount && <div className="h-6 mb-4"></div>}

                <h3 className="text-lg sm:text-xl font-bold text-zinc-900 dark:text-zinc-100 mb-2">{plan.name}</h3>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-2xl font-black text-zinc-400 line-through">{plan.price}</span>
                  <span className="text-3xl sm:text-4xl font-black text-emerald-600 dark:text-emerald-400">$0</span>
                  <span className="text-base text-zinc-500">{plan.period}</span>
                </div>
                <p className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold mb-6">Gratis por tiempo limitado</p>

                <ul className="space-y-3 flex-1 mb-6">
                  {plan.features.map((feat, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-zinc-700 dark:text-zinc-300">
                      <CheckCircle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" weight="fill" />
                      {feat}
                    </li>
                  ))}
                </ul>

                <Link to="/register" className="mt-auto">
                  <Button className={`w-full rounded-none h-11 ${plan.popular ? 'btn-primary' : 'bg-zinc-900 text-white hover:bg-zinc-800 dark:bg-zinc-700 dark:hover:bg-zinc-600'}`}>
                    Acceder Gratis
                  </Button>
                </Link>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-zinc-400 mt-6">
            Precios regulares mostrados como referencia. Actualmente todo el acceso es gratuito por periodo de lanzamiento.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="py-14 sm:py-24 bg-zinc-100 dark:bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 dark:text-zinc-100 mb-3 sm:mb-4">
              ¿Cómo funciona?
            </h2>
            <p className="text-base sm:text-lg text-zinc-600 dark:text-zinc-400 max-w-2xl mx-auto">
              En tres simples pasos estarás recibiendo propuestas de proveedores
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
            {[
              { step: '01', title: 'Crea tu proyecto', desc: 'Sube la lista de materiales que necesitas, con cantidades y unidades.' },
              { step: '02', title: 'Recibe propuestas', desc: 'Los proveedores especializados te enviarán sus mejores ofertas.' },
              { step: '03', title: 'Elige y contrata', desc: 'Compara propuestas, selecciona la mejor y contacta directamente.' },
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="text-6xl sm:text-8xl font-bold text-zinc-200 dark:text-zinc-800 absolute -top-2 sm:-top-4 left-0">{item.step}</div>
                <div className="relative pt-12 sm:pt-16 pl-4">
                  <h3 className="text-lg sm:text-xl font-bold text-zinc-900 dark:text-zinc-100 mb-2 sm:mb-3">{item.title}</h3>
                  <p className="text-sm sm:text-base text-zinc-600 dark:text-zinc-400">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Matchito Section */}
      <section className="py-14 sm:py-20 bg-white dark:bg-zinc-950">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center gap-8 sm:gap-12">
            <div className="w-48 h-48 sm:w-64 sm:h-64 flex-shrink-0 bg-orange-50 dark:bg-orange-950/20 rounded-2xl flex items-center justify-center overflow-hidden border-2 border-orange-200 dark:border-orange-800">
              <img
                src="/matchito.gif"
                alt="Matchito - Asistente Virtual de MaterMatch"
                className="w-full h-full object-cover scale-110"
              />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-zinc-100 mb-3">
                Conoce a <span className="text-orange-500">Matchito</span>
              </h2>
              <p className="text-base sm:text-lg text-zinc-600 dark:text-zinc-400 mb-4">
                Matchito es nuestro asistente virtual que te acompaña en cada paso de tu proceso. 
                Desde crear un proyecto hasta cerrar un trato, Matchito celebra contigo cada logro 
                y te guía en la plataforma.
              </p>
              <div className="flex flex-wrap gap-2">
                {['Proyectos creados', 'Propuestas recibidas', 'Compras confirmadas', 'Contratos firmados'].map((tag, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-xs font-semibold px-3 py-1.5">
                    <Handshake className="w-3.5 h-3.5" />
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-14 sm:py-24 bg-zinc-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
            Comienza a optimizar tus compras hoy
          </h2>
          <p className="text-base sm:text-lg text-zinc-400 mb-6 sm:mb-8">
            Únete a miles de constructores que ya confían en MaterMatch
          </p>
          <Link to="/register">
            <Button className="btn-primary rounded-none h-12 sm:h-14 px-8 sm:px-12 text-base sm:text-lg w-full sm:w-auto">
              Crear Cuenta Gratis
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <p className="text-zinc-500 mt-4 text-sm">
            Sin tarjeta de crédito. Sin compromisos.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white dark:bg-zinc-900 border-t border-zinc-200 dark:border-zinc-800 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img 
                src="/matermatch-logo.png" 
                alt="MaterMatch" 
                className="h-10 w-auto"
              />
              <div>
                <span className="text-lg font-black text-zinc-600 dark:text-zinc-300 tracking-tight">Mater</span>
                <span className="text-lg font-black text-orange-500 tracking-tight">Match</span>
              </div>
            </div>
            <p className="text-zinc-500 dark:text-zinc-400 text-sm font-medium tracking-wide">
              Cotiza. Compara. Construye.
            </p>
            <p className="text-zinc-400 text-sm">
              &copy; 2025 MaterMatch. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
